import { Maximize2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { FindingKind } from "./workspace-types";

export interface WorkspaceFindingsToolbarProps {
  problemsCount: number;
  helpsCount: number;
  totalFindingsCount: number;
  kindFilter: FindingKind;
  onKindFilterChange: (kind: FindingKind) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  severityFilter: string;
  onSeverityFilterChange: (severity: string) => void;
  showZenButton: boolean;
  onOpenZenMode?: () => void;
}

export function WorkspaceFindingsToolbar({
  problemsCount,
  helpsCount,
  totalFindingsCount,
  kindFilter,
  onKindFilterChange,
  statusFilter,
  onStatusFilterChange,
  severityFilter,
  onSeverityFilterChange,
  showZenButton,
  onOpenZenMode,
}: WorkspaceFindingsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {/* Segmented Filter Pills */}
      <div className="flex items-center gap-1 rounded-lg border border-border/80 bg-muted/40 p-1 w-full sm:w-auto">
        <button
          type="button"
          onClick={() => onKindFilterChange("problem")}
          className={cn(
            "flex-1 sm:flex-initial rounded-md px-3 py-1 text-xs font-semibold transition-colors text-center cursor-pointer",
            kindFilter === "problem"
              ? "bg-foreground text-background shadow-xs font-semibold"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Problems ({problemsCount})
        </button>
        <button
          type="button"
          onClick={() => onKindFilterChange("strength")}
          className={cn(
            "flex-1 sm:flex-initial rounded-md px-3 py-1 text-xs font-semibold transition-colors text-center cursor-pointer",
            kindFilter === "strength"
              ? "bg-foreground text-background shadow-xs font-semibold"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Strengths ({helpsCount})
        </button>
        <button
          type="button"
          onClick={() => onKindFilterChange("all")}
          className={cn(
            "flex-1 sm:flex-initial rounded-md px-3 py-1 text-xs font-semibold transition-colors text-center cursor-pointer",
            kindFilter === "all"
              ? "bg-foreground text-background shadow-xs font-semibold"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          All ({totalFindingsCount})
        </button>
      </div>

      {/* Status & Severity Selects + Zen Review */}
      <div className="flex items-center gap-2 w-full sm:w-auto">
        {kindFilter !== "strength" && (
          <>
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger
                className="h-8 flex-1 sm:w-[120px] text-xs"
                aria-label="Filter by status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="addressed">Addressed</SelectItem>
                <SelectItem value="dismissed">Set aside / Dismissed</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={severityFilter}
              onValueChange={onSeverityFilterChange}
            >
              <SelectTrigger
                className="h-8 flex-1 sm:w-[125px] text-xs"
                aria-label="Filter by severity"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="major">Major</SelectItem>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="nit">Nit</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}

        {showZenButton && onOpenZenMode && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenZenMode}
            className="gap-1.5 text-xs font-semibold shadow-2xs shrink-0"
            title="Inspect findings in fullscreen Zen mode (screenshots, annotations, persona voice)"
          >
            <Maximize2 className="size-3.5 text-primary" />
            <span className="hidden sm:inline">Zen Review</span>
            <span className="sm:hidden">Zen</span>
          </Button>
        )}
      </div>
    </div>
  );
}
